/**
 * Network health status types
 */
export enum NetworkHealthStatus {
    CONNECTED = 'connected',
    DISCONNECTED = 'disconnected',
    UNKNOWN = 'unknown'
}

/**
 * VPN lock error codes that trigger the overlay
 */
export const VPN_LOCK_ERROR_CODES = [
    'ERR_NETWORK',
    'ECONNABORTED',
    'ENOTFOUND',
    'ECONNREFUSED',
    'ETIMEDOUT',
    'ERR_CONNECTION_REFUSED',
    'ERR_NAME_NOT_RESOLVED'
] as const;

/**
 * HTTP status codes that trigger VPN lock
 */
export const VPN_LOCK_STATUS_CODES = [403] as const;

/**
 * Callback type for network status changes
 */
export type NetworkStatusCallback = (status: NetworkHealthStatus, error?: string) => void;

/**
 * Configuration for the network health service
 */
export interface NetworkHealthConfig {
    /** Whether to lock on 403 errors (default: true) */
    lockOn403: boolean;
    /** Whether to lock on network errors (default: true) */
    lockOnNetworkError: boolean;
}

const DEFAULT_CONFIG: NetworkHealthConfig = {
    lockOn403: true,
    lockOnNetworkError: true
};

/**
 * NetworkHealthService - Manages VPN/network connectivity state.
 * 
 * This service detects VPN disconnection from failed API calls rather than
 * using a dedicated healthcheck endpoint. It triggers a locked UI state when:
 * - 403 Forbidden (Access Restrictions block)
 * - Network errors / timeouts (VPN disconnected)
 * 
 * Usage:
 * ```typescript
 * // Get instance
 * const healthService = NetworkHealthService.getInstance();
 * 
 * // Listen for status changes
 * healthService.addStatusListener((status, error) => {
 *     if (status === NetworkHealthStatus.DISCONNECTED) {
 *         // Show VPN warning, block UI
 *     }
 * });
 * 
 * // In axios error handler:
 * healthService.reportApiFailure(error);
 * 
 * // On successful API call:
 * healthService.reportApiSuccess();
 * ```
 */
export class NetworkHealthService {
    private static instance: NetworkHealthService | null = null;
    
    private config: NetworkHealthConfig;
    private currentStatus: NetworkHealthStatus = NetworkHealthStatus.UNKNOWN;
    private listeners: Set<NetworkStatusCallback> = new Set();
    private lastError: string | undefined;
    private hasEverConnected: boolean = false;

    private constructor(config?: Partial<NetworkHealthConfig>) {
        this.config = { ...DEFAULT_CONFIG, ...config };
    }

    /**
     * Get the singleton instance of NetworkHealthService
     */
    public static getInstance(config?: Partial<NetworkHealthConfig>): NetworkHealthService {
        if (!NetworkHealthService.instance) {
            NetworkHealthService.instance = new NetworkHealthService(config);
        }
        return NetworkHealthService.instance;
    }

    /**
     * Reset the singleton instance (useful for testing)
     */
    public static resetInstance(): void {
        NetworkHealthService.instance = null;
    }

    /**
     * Get current network health status
     */
    public getStatus(): NetworkHealthStatus {
        return this.currentStatus;
    }

    /**
     * Get last error message if any
     */
    public getLastError(): string | undefined {
        return this.lastError;
    }

    /**
     * Check if we've ever had a successful connection
     */
    public hasConnectedBefore(): boolean {
        return this.hasEverConnected;
    }

    /**
     * Add a listener for status changes
     */
    public addStatusListener(callback: NetworkStatusCallback): void {
        this.listeners.add(callback);
    }

    /**
     * Remove a status listener
     */
    public removeStatusListener(callback: NetworkStatusCallback): void {
        this.listeners.delete(callback);
    }

    /**
     * Notify all listeners of status change
     */
    private notifyListeners(status: NetworkHealthStatus, error?: string): void {
        this.listeners.forEach(listener => {
            try {
                listener(status, error);
            } catch (e) {
                console.error('[NetworkHealth] Error in listener:', e);
            }
        });
    }

    /**
     * Update status and notify listeners if changed
     */
    private updateStatus(newStatus: NetworkHealthStatus, error?: string): void {
        const previousStatus = this.currentStatus;
        this.currentStatus = newStatus;
        this.lastError = error;

        if (previousStatus !== newStatus) {
            console.log(`[NetworkHealth] Status changed: ${previousStatus} → ${newStatus}`);
            this.notifyListeners(newStatus, error);
        }
    }

    /**
     * Report a successful API call - marks network as connected
     */
    public reportApiSuccess(): void {
        this.hasEverConnected = true;
        if (this.currentStatus !== NetworkHealthStatus.CONNECTED) {
            this.updateStatus(NetworkHealthStatus.CONNECTED);
        }
    }

    /**
     * Report an API failure - checks if it's a VPN-related error
     * Returns true if this error should trigger VPN lock
     */
    public reportApiFailure(error: unknown): boolean {
        const vpnError = this.isVpnRelatedError(error);
        
        if (vpnError.isVpnError) {
            console.warn('[NetworkHealth] VPN-related error detected:', vpnError.message);
            this.updateStatus(NetworkHealthStatus.DISCONNECTED, vpnError.message);
            return true;
        }
        
        return false;
    }

    /**
     * Manually set status to connected (e.g., after successful retry)
     */
    public markConnected(): void {
        this.hasEverConnected = true;
        this.updateStatus(NetworkHealthStatus.CONNECTED);
    }

    /**
     * Manually set status to disconnected (e.g., for testing)
     */
    public markDisconnected(error?: string): void {
        this.updateStatus(NetworkHealthStatus.DISCONNECTED, error || 'VPN connection required');
    }

    /**
     * Check if an error is VPN-related (should trigger lock UI)
     */
    public isVpnRelatedError(error: unknown): { isVpnError: boolean; message: string } {
        // Check for axios errors
        if (this.isAxiosError(error)) {
            const axiosError = error as any;
            
            // Check HTTP status codes that indicate VPN/access issues
            if (axiosError.response?.status) {
                const status = axiosError.response.status;
                
                if (status === 403 && this.config.lockOn403) {
                    return {
                        isVpnError: true,
                        message: 'Access denied (403) - VPN connection required'
                    };
                }
                
            }
            
            // Check error codes that indicate network issues
            if (this.config.lockOnNetworkError) {
                const errorCode = axiosError.code;
                if (errorCode && VPN_LOCK_ERROR_CODES.includes(errorCode as any)) {
                    return {
                        isVpnError: true,
                        message: this.getNetworkErrorMessage(errorCode)
                    };
                }
                
                // Check for timeout
                if (errorCode === 'ECONNABORTED' || axiosError.message?.includes('timeout')) {
                    return {
                        isVpnError: true,
                        message: 'Request timeout - VPN may be disconnected'
                    };
                }

                // Check for network error (no response received)
                if (!axiosError.response && axiosError.request) {
                    return {
                        isVpnError: true,
                        message: 'Network error - Unable to reach server. Check VPN connection.'
                    };
                }
            }
        }
        
        // Check for generic network errors
        if (error instanceof TypeError && error.message.includes('Network')) {
            return {
                isVpnError: true,
                message: 'Network error - Check your VPN connection'
            };
        }
        
        // Not a VPN-related error
        return {
            isVpnError: false,
            message: error instanceof Error ? error.message : 'Unknown error'
        };
    }

    /**
     * Check if error is an axios error
     */
    private isAxiosError(error: unknown): boolean {
        if (error === null || error === undefined || typeof error !== 'object') {
            return false;
        }
        const errorObj = error as Record<string, unknown>;
        return 'isAxiosError' in errorObj && errorObj.isAxiosError === true;
    }

    /**
     * Get user-friendly message for network error codes
     */
    private getNetworkErrorMessage(code: string): string {
        switch (code) {
            case 'ERR_NETWORK':
                return 'Network error - VPN connection lost';
            case 'ECONNABORTED':
                return 'Connection aborted - Request timed out';
            case 'ENOTFOUND':
                return 'Server not found - Check VPN connection';
            case 'ECONNREFUSED':
                return 'Connection refused - Server unreachable';
            case 'ETIMEDOUT':
                return 'Connection timed out - VPN may be disconnected';
            case 'ERR_CONNECTION_REFUSED':
                return 'Connection refused - Check VPN connection';
            case 'ERR_NAME_NOT_RESOLVED':
                return 'DNS error - Check network connection';
            default:
                return `Network error (${code}) - Check VPN connection`;
        }
    }

    /**
     * Attempt to reconnect by marking status as unknown
     * The next API call will determine actual status
     */
    public async attemptReconnect(): Promise<void> {
        // Reset the error state and mark status as unknown
        this.lastError = undefined;
        this.updateStatus(NetworkHealthStatus.UNKNOWN);
    }
}

export default NetworkHealthService;
